import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import Button from '../../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactRef,
} from '../../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {presentationProtectionSelector, selectedPresentationMetaSelector} from '../../../../../../store/selectors/presentation.selectors';
import {selectedSlideHiddenSelector, selectedSlideIdSelector} from '../../../../../../store/selectors/slide.selectors';
import {createPlayPresentationUrl, isPresentationRunnable} from '../../../../../../utils/presentation';

type MenuBarRunDropdownProps = {
    runMenuItemRef: React.RefObject<DropdownMenuCompactRef>;
    menuBarOpen: boolean;
    onHoverEnter: () => void;
    onMenuClose: () => void;
    onOpenMenuBar: () => void;
    onOpenCreateRoomModal: () => void;
};

function MenuBarRunDropdown({
    runMenuItemRef,
    menuBarOpen,
    onHoverEnter,
    onMenuClose,
    onOpenMenuBar,
    onOpenCreateRoomModal,
}: MenuBarRunDropdownProps): JSX.Element {
    const {t} = useTranslation();
    const presentationProtection = useSelector(presentationProtectionSelector);
    const selectedPresentationMeta = useSelector(selectedPresentationMetaSelector);
    const slideHidden = useSelector(selectedSlideHiddenSelector);
    const slideId = useSelector(selectedSlideIdSelector);

    const runMenuItems = useMemo(
        () => [
            [
                {
                    label: t('menuBar.runMenu.presentation'),
                    icon: 'solar:play-line-duotone',
                    onClick: () => window.open(createPlayPresentationUrl(selectedPresentationMeta), '_blank', 'noopener,noreferrer'),
                },
                {
                    label: t('menuBar.runMenu.slide'),
                    icon: 'solar:hashtag-square-linear',
                    disabled: !isPresentationRunnable(presentationProtection?.types) || slideHidden,
                    onClick: () =>
                        window.open(
                            createPlayPresentationUrl(selectedPresentationMeta, undefined, undefined, false, slideId),
                            '_blank',
                            'noopener,noreferrer'
                        ),
                },
                {
                    label: t('menuBar.runMenu.room'),
                    icon: 'solar:microphone-2-linear',
                    onClick: onOpenCreateRoomModal,
                },
            ],
        ],
        [t, presentationProtection?.types, selectedPresentationMeta, slideHidden, slideId, onOpenCreateRoomModal]
    );

    return (
        <DropdownMenuCompact
            ref={runMenuItemRef}
            openOnMouseEnter={menuBarOpen}
            onHoverEnter={onHoverEnter}
            onMenuClose={onMenuClose}
            items={runMenuItems}
        >
            <Button size="2xs" disableHover square variant="ghost" className="c-menuBar__button" onClick={onOpenMenuBar}>
                {t('menuBar.run')}
            </Button>
        </DropdownMenuCompact>
    );
}

export default React.memo(MenuBarRunDropdown);
